import {z} from 'zod';

import {
  BaseVoidResponseSchema,
  CreatedAtAndUpdatedBySchema,
  HeaderDynamicActionsFlagsSchema,
  PaymentTypeSchema,
} from '@dms/api/shared';

const ReceiveNoteCorrectionStateSchema = z.enum(['PENDING', 'COMPLETED']);

const ReceiveNoteCorrectionTotalPriceSchema = z.object({
  totalPriceWithoutVat: z.number(),
  totalPriceWithVat: z.number(),
});

export type ReceiveNoteCorrectionTotalPrice = z.infer<typeof ReceiveNoteCorrectionTotalPriceSchema>;

/**
 * GET
 */
export const GetReceiveNoteCorrectionRequestSchema = z.object({
  creditNoteId: z.string(),
});
export type GetReceiveNoteCorrectionRequest = z.infer<typeof GetReceiveNoteCorrectionRequestSchema>;

export const GetReceiveNoteCorrectionResponseSchema = z.object({
  creditNote: z
    .object({
      creditNoteId: z.string(),
      creditNoteNumber: z.string().optional(),
      supplierId: z.string(),
      warehouseId: z.string(),
      authorizationProfileId: z.string(),
      branchId: z.string(),
      deliveryNoteNumber: z.string().optional(),
      deliveryNoteIssueDate: z.string().optional(),
      correctionDeliveryNoteNumber: z.string().nullish(),
      correctionDeliveryNoteDate: z.string().nullish(),
      correctionInvoiceNumber: z.string().optional(),
      correctionInvoiceDate: z.string().optional(),
      paymentType: PaymentTypeSchema,
      currency: z.string().optional(),
      exchangeRate: z.number().optional(),
      ncConversion: z.string().optional(),
      state: ReceiveNoteCorrectionStateSchema,
      note: z.string().optional(),
      completedAt: z.string().optional(),
      completedBy: z.string().optional(),
      correctedCreditNoteId: z.string().optional(),
      markers: HeaderDynamicActionsFlagsSchema.optional(),
    })
    .merge(CreatedAtAndUpdatedBySchema),
  receiveNote: z.object({
    receiveNoteId: z.string(),
    receiveNoteNumber: z.string().optional(),
    supplierId: z.string(),
    warehouseId: z.string(),
    authorizationProfileId: z.string(),
    branchId: z.string(),
    deliveryNoteId: z.string(),
    deliveryNoteNumber: z.string().optional(),
    deliveryNoteIssueDate: z.string().optional(),
    note: z.string().optional(),
    createdAt: z.string(),
    completedAt: z.string().optional(),
    isFromReceiveNote: z.boolean(),
    invoiceNumber: z.string().optional(),
    invoiceIssueDate: z.string().optional(),
  }),
  total: ReceiveNoteCorrectionTotalPriceSchema,
});
export type GetReceiveNoteCorrectionResponse = z.infer<
  typeof GetReceiveNoteCorrectionResponseSchema
>;

/**
 * POST requests
 */
export const PostReceiveNoteCorrectionRequestSchema = z.object({
  body: z.object({
    receiveNoteId: z.string(),
    supplierId: z.string(),
    warehouseId: z.string(),
    correctionDeliveryNoteNumber: z.string().nullish(),
    correctionDeliveryNoteDate: z.string().nullish(),
    correctionInvoiceNumber: z.string().nullish(),
    invoiceIssueDate: z.string().nullish(),
    paymentType: PaymentTypeSchema,
    currency: z.string().nullish(),
    exchangeRate: z.number().nullish(),
    ncConversion: z.string().nullish(),
    note: z.string().nullish(),
  }),
});
export type PostReceiveNoteCorrectionRequest = z.infer<
  typeof PostReceiveNoteCorrectionRequestSchema
>;

export const PostReceiveNoteCorrectionResponseSchema = z.object({
  id: z.string(),
});
export type PostReceiveNoteCorrectionResponse = z.infer<
  typeof PostReceiveNoteCorrectionResponseSchema
>;

/**
 * PATCH requests
 */
export const PatchReceiveNoteCorrectionRequestSchema = z.object({
  creditNoteId: z.string(),
  body: z.object({
    warehouseId: z.string(),
    supplierId: z.string(),
    correctionDeliveryNoteNumber: z.string().nullish(),
    correctionDeliveryNoteDate: z.string().nullish(),
    correctionInvoiceNumber: z.string().nullish(),
    invoiceIssueDate: z.string().nullish(),
    paymentType: PaymentTypeSchema,
    currency: z.string().nullish(),
    exchangeRate: z.number().nullish(),
    ncConversion: z.string().nullish(),
    note: z.string().nullish(),
  }),
});
export type PatchReceiveNoteCorrectionRequest = z.infer<
  typeof PatchReceiveNoteCorrectionRequestSchema
>;

export const PatchReceiveNoteCorrectionResponseSchema = BaseVoidResponseSchema;
export type PatchReceiveNoteCorrectionResponse = z.infer<
  typeof PatchReceiveNoteCorrectionResponseSchema
>;

export const PatchCompletedReceiveNoteCorrectionRequestSchema = z.object({
  creditNoteId: z.string(),
  body: z.object({
    correctionDeliveryNoteNumber: z.string().nullish(),
    correctionDeliveryNoteDate: z.string().nullish(),
    correctionInvoiceNumber: z.string().nullish(),
    invoiceIssueDate: z.string().nullish(),
    paymentType: PaymentTypeSchema.nullish(),
    note: z.string().nullish(),
  }),
});
export type PatchCompletedReceiveNoteCorrectionRequest = z.infer<
  typeof PatchCompletedReceiveNoteCorrectionRequestSchema
>;

export const PatchCompletedReceiveNoteCorrectionResponseSchema = BaseVoidResponseSchema;
export type PatchCompletedReceiveNoteCorrectionResponse = z.infer<
  typeof PatchCompletedReceiveNoteCorrectionResponseSchema
>;

export const PatchReceiveNoteCorrectionDeleteRequestSchema = z.object({
  body: z.object({
    creditNoteId: z.array(z.string()),
  }),
});
export type PatchReceiveNoteCorrectionDeleteRequest = z.infer<
  typeof PatchReceiveNoteCorrectionDeleteRequestSchema
>;

export const PatchReceiveNoteCorrectionDeleteResponseSchema = BaseVoidResponseSchema;
export type PatchReceiveNoteCorrectionDeleteResponse = z.infer<
  typeof PatchReceiveNoteCorrectionDeleteResponseSchema
>;

/**
 * PUT requests
 */

export const PutReceiveNoteCorrectionActionRequestSchema = z.object({
  id: z.string(),
  body: z.object({
    actionKey: z.string(),
  }),
});
export type PutReceiveNoteCorrectionActionRequest = z.infer<
  typeof PutReceiveNoteCorrectionActionRequestSchema
>;

export const PutReceiveNoteCorrectionActionResponseSchema = BaseVoidResponseSchema;
export type PutReceiveNoteCorrectionActionResponse = z.infer<
  typeof PutReceiveNoteCorrectionActionResponseSchema
>;
